/**
 * Authentication Service
 * Handles user authentication, registration, and token management
 */

import { apiClient, extractData, withRetry } from './api';
import type { User, LoginRequest, LoginResponse, RegisterRequest } from '@kids-lms/types';

export interface VerifyEmailResponse {
  verified: boolean;
}

export interface ForgotPasswordResponse {
  sent: boolean;
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ResetPasswordResponse {
  reset: boolean;
}

/**
 * Register a new user (parent or teacher)
 */
export async function register(data: RegisterRequest): Promise<User> {
  const response = await withRetry(() =>
    apiClient.post<{ success: boolean; data: User }>('/auth/register', data)
  );
  return extractData(response);
}

/**
 * Login user
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  const response = await withRetry(() =>
    apiClient.post<{ success: boolean; data: LoginResponse }>('/auth/login', data)
  );
  return extractData(response);
}

/**
 * Refresh access token
 */
export async function refreshToken(refreshToken: string): Promise<{ accessToken: string; expiresIn: number }> {
  const response = await apiClient.post<{ success: boolean; data: { accessToken: string; expiresIn: number } }>(
    '/auth/refresh',
    { refreshToken }
  );
  return extractData(response);
}

/**
 * Logout user
 */
export async function logout(): Promise<{ loggedOut: boolean }> {
  const response = await apiClient.post<{ success: boolean; data: { loggedOut: boolean } }>('/auth/logout');
  return extractData(response);
}

/**
 * Verify email address
 */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const response = await apiClient.post<{ success: boolean; data: VerifyEmailResponse }>(
    '/auth/verify-email',
    { token }
  );
  return extractData(response);
}

/**
 * Request password reset email
 */
export async function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
  const response = await apiClient.post<{ success: boolean; data: ForgotPasswordResponse }>(
    '/auth/forgot-password',
    { email }
  );
  return extractData(response);
}

/**
 * Reset password with token
 */
export async function resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  const response = await apiClient.post<{ success: boolean; data: ResetPasswordResponse }>(
    '/auth/reset-password',
    data
  );
  return extractData(response);
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<{ success: boolean; data: User }>('/users/me');
  return extractData(response);
}

/**
 * Update user profile
 */
export async function updateProfile(data: Partial<User>): Promise<User> {
  const response = await apiClient.patch<{ success: boolean; data: User }>('/users/me', data);
  return extractData(response);
}

export default {
  register,
  login,
  refreshToken,
  logout,
  verifyEmail,
  forgotPassword,
  resetPassword,
  getCurrentUser,
  updateProfile,
};
